<template>
    <!--
        StockTable.vue — Reusable stock listing component
        ─────────────────────────────────────────────────
        Props
          items[]        Full flat dataset; filtering + pagination done client-side
          filterTypes[]  Options for the type dropdown
          loading        Shows shimmer skeleton while true
          perPage        Rows per page (default 8)
          placeholder    Search input hint text

        Slots
          #header-end    Inject buttons / extras into the right side of the header bar
    -->
    <div>

        <!-- ╔══════════════════════════════╗
             ║  Header                      ║
             ╚══════════════════════════════╝ -->
        <div class="d-flex align-items-center justify-content-between px-4 py-3 border-bottom">
            <div class="d-flex align-items-center gap-2">
                <span class="text-uppercase fw-black text-dark lh-1">
                    Stock Items
                </span>
                <!-- pill count — dark bg not available as a BS5 bg-* utility -->
                <span class="count-pill">{{ filteredItems.length }}</span>
            </div>
            <slot name="header-end" />
        </div>

        <!-- ╔══════════════════════════════╗
             ║  Search + Type filter        ║
             ╚══════════════════════════════╝ -->
        <div class="d-flex gap-2 px-4 py-2  border-bottom">

            <!-- search -->
            <div class="position-relative flex-grow-1">
                <!-- icon — cannot be placed via BS5 alone -->
                <span class="position-absolute top-50 translate-middle-y ps-2 text-secondary lh-1">
                    <svg width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.6">
                        <circle cx="6.5" cy="6.5" r="4"/><path d="M10.5 10.5l3 3"/>
                    </svg>
                </span>
                <input
                    v-model="searchQuery"
                    type="text"
                    class="form-control form-control-lg ps-4 border-0 bg-white shadow-none"
                    style="font-size:12px"
                    :placeholder="placeholder"
                    @input="resetPage"
                />
            </div>

            <!-- type filter -->
            <select
                v-model="activeType"
                class="form-select form-select-lg rounde-0 border-0 bg-white shadow-none"
                style="min-width:30px;font-size:12px"
                @change="resetPage"
            >
                <option value="">All Types</option>
                <option v-for="t in filterTypes" :key="t" :value="t">{{ t }}</option>
            </select>

        </div>

        <!-- ╔══════════════════════════════╗
             ║  Table                       ║
             ╚══════════════════════════════╝ -->
        <div class="table-responsive px-4">
            <table class="table table-hover table-sm align-middle mb-0">

                <thead>
                    <tr>
                        <!-- BS5 has no text-[10px] utility; letter-spacing also not a utility -->
                        <th class="col-idx text-dark fw-semibold fs-6 text-uppercase tbl-th">#</th>
                        <th class="text-dark fw-semibold text-uppercase fs-6 tbl-th">Serial Number</th>
                        <th class="text-dark fw-semibold text-uppercase fs-6 ">Stock Type</th>
                        <th class="text-dark fw-semibold text-uppercase fs-6 text-end">Date Added</th>
                    </tr>
                </thead>

                <!-- ── skeleton ── -->
                <tbody v-if="loading">
                    <tr v-for="n in perPage" :key="n" class="border-bottom">
                        <td><div class="skel" style="width:18px"></div></td>
                        <td><div class="skel" style="width:130px"></div></td>
                        <td><div class="skel" style="width:80px"></div></td>
                        <td class="text-end"><div class="skel ms-auto" style="width:70px"></div></td>
                    </tr>
                </tbody>

                <!-- ── rows ── -->
                <tbody v-else-if="pagedItems.length">
                    <tr v-for="(item, i) in pagedItems" :key="item.id">
                        <td class="text-dark" style="font-size:11.5px;font-family:var(--font-mono)">
                            {{ offset + i + 1 }}
                        </td>
                        <td class="fw-medium" style="font-size:13px;font-family:var(--font-mono)">
                            {{ item.serial }}
                        </td>
                        <td>
                            <!-- badge: custom bg not in BS5 palette -->
                            <span class="type-pill ">{{ item.type }}</span>
                        </td>
                        <td class="text-end text-dark" style="font-size:12.5px;font-family:var(--font-mono)">
                            {{ formatDate(item.created_at) }}
                        </td>
                    </tr>
                </tbody>

                <!-- ── empty ── -->
                <tbody v-else>
                    <tr>
                        <td colspan="4" class="text-center text-secondary py-5">
                            <div class="d-flex flex-column align-items-center gap-2">
                                <svg width="26" height="26" viewBox="0 0 24 24" fill="none"
                                     stroke="currentColor" stroke-width="1.3" opacity=".4">
                                    <rect x="2" y="7" width="20" height="14" rx="2"/>
                                    <path d="M16 7V5a2 2 0 00-2-2h-4a2 2 0 00-2 2v2"/>
                                    <line x1="12" y1="12" x2="12" y2="16"/>
                                    <line x1="10" y1="14" x2="14" y2="14"/>
                                </svg>
                                <span style="font-size:12px">No stock items match your search.</span>
                            </div>
                        </td>
                    </tr>
                </tbody>

            </table>
        </div>

        <!-- ╔══════════════════════════════╗
             ║  Pagination                  ║
             ╚══════════════════════════════╝ -->
        <div
            v-if="!loading && filteredItems.length > 0"
            class="d-flex align-items-center justify-content-between px-4 py-3 border-top"
        >
            <span class="text-secondary" style="font-size:11px">
                Showing {{ rangeStart }}–{{ rangeEnd }} of {{ filteredItems.length }} items
            </span>

            <nav>
                <ul class="pagination pagination-sm mb-0 gap-1">

                    <li class="page-item" :class="{ disabled: currentPage === 1 }">
                        <button class="page-link pg-btn border rounded" @click="goPage(currentPage - 1)">
                            <svg width="11" height="11" viewBox="0 0 12 12" fill="none"
                                 stroke="currentColor" stroke-width="1.8"><path d="M8 2L4 6l4 4"/></svg>
                        </button>
                    </li>

                    <template v-for="p in pageRange" :key="String(p)">
                        <li v-if="p === '…'" class="page-item disabled">
                            <span class="page-link pg-btn border-0 text-secondary" style="font-size:11px">…</span>
                        </li>
                        <li v-else class="page-item" :class="{ active: p === currentPage }">
                            <button class="page-link pg-btn border rounded" @click="goPage(+p)">{{ p }}</button>
                        </li>
                    </template>

                    <li class="page-item" :class="{ disabled: currentPage === totalPages }">
                        <button class="page-link pg-btn border rounded" @click="goPage(currentPage + 1)">
                            <svg width="11" height="11" viewBox="0 0 12 12" fill="none"
                                 stroke="currentColor" stroke-width="1.8"><path d="M4 2l4 4-4 4"/></svg>
                        </button>
                    </li>

                </ul>
            </nav>
        </div>

    </div>
</template>

<script lang="ts">
import { Component, Vue, toNative, Prop } from 'vue-facing-decorator'

export interface StockItem {
    id: number
    serial: string
    type: string
    created_at: string
}

@Component
class StockTable extends Vue {

    @Prop({ type: Array, default: () => [] })
    readonly items!: StockItem[]

    @Prop({ type: Array, default: () => [] })
    readonly filterTypes!: string[]

    @Prop({ type: Boolean, default: false })
    readonly loading!: boolean

    @Prop({ type: Number, default: 8 })
    readonly perPage!: number

    @Prop({ type: String, default: 'Search serial number or type…' })
    readonly placeholder!: string

    searchQuery = ''
    activeType  = ''
    currentPage = 1

    get filteredItems(): StockItem[] {
        const q = this.searchQuery.toLowerCase().trim()
        const t = this.activeType
        return this.items.filter(row =>
            (!q || row.serial.toLowerCase().includes(q) || row.type.toLowerCase().includes(q)) &&
            (!t || row.type === t)
        )
    }

    get totalPages(): number {
        return Math.max(1, Math.ceil(this.filteredItems.length / this.perPage))
    }

    get offset(): number {
        return (this.currentPage - 1) * this.perPage
    }

    get pagedItems(): StockItem[] {
        return this.filteredItems.slice(this.offset, this.offset + this.perPage)
    }

    get rangeStart(): number {
        return this.filteredItems.length ? this.offset + 1 : 0
    }

    get rangeEnd(): number {
        return Math.min(this.offset + this.perPage, this.filteredItems.length)
    }

    get pageRange(): (number | '…')[] {
        const c = this.currentPage, t = this.totalPages
        if (t <= 7) return Array.from({ length: t }, (_, i) => i + 1)
        if (c <= 4)         return [1, 2, 3, 4, 5, '…', t]
        if (c >= t - 3)     return [1, '…', t-4, t-3, t-2, t-1, t]
        return [1, '…', c-1, c, c+1, '…', t]
    }

    resetPage() { this.currentPage = 1 }

    goPage(p: number) {
        if (p >= 1 && p <= this.totalPages) this.currentPage = p
    }

    formatDate(d?: string | null): string {
        if (!d) return '—'
        return new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
    }
}

export default toNative(StockTable)
</script>

<style scoped>
/*
  Only rules that Bootstrap 5 utilities cannot express:
  1. custom font token (serif/mono) — no BS5 utility
  2. count-pill: dark (#0d0e12) bg — not a BS5 bg-* colour
  3. tbl-th: 10px font-size + letter-spacing — no BS5 utility
  4. type-pill: custom off-white bg (#f0efe9) — not BS5
  5. skel shimmer animation — not expressible as a utility
  6. pg-btn exact 28×28px square + active dark override
  7. col-idx: 36px column width — no BS5 utility
*/

/* tokens (consumed via inline style="font-family:var(--font-mono)") */
:root {
    --font-mono:  'DM Mono', 'Courier New', monospace;
    --font-serif: 'DM Serif Display', Georgia, serif;
    --ink:        #0d0e12;
    --border-col: #e8e7e2;
}

.count-pill {
    font-size: 10px;
    font-family: var(--font-mono);
    background: var(--ink);
    color: #fff;
    border-radius: 999px;
    padding: 1px 8px;
    line-height: 1.7;
}

.tbl-th {
    font-size: 10px;
    letter-spacing: .08em;
    border-bottom: 1.5px solid var(--ink) !important;
    padding-top: 9px;
    padding-bottom: 9px;
}

.col-idx { width: 36px; }

.type-pill {
    font-size: 10.5px;
    font-weight: 500;
    background: #f0efe9;
    color: #4a4d5e;
    border-radius: 4px;
    padding: 2px 8px;
}

/* skeleton shimmer — animation cannot be a utility */
.skel {
    height: 11px;
    border-radius: 3px;
    background: linear-gradient(90deg, #f0efe9 25%, #e8e7e2 50%, #f0efe9 75%);
    background-size: 200% 100%;
    animation: shimmer 1.2s infinite;
}
@keyframes shimmer { to { background-position: -200% 0; } }

/* pagination buttons: exact 28×28 square, active dark fill */
.pg-btn {
    width: 28px;
    height: 28px;
    padding: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 12px;
    border-color: var(--border-col) !important;
    border-radius: 5px !important;
    color: var(--ink);
    background: #fff;
    transition: background .12s, border-color .12s;
}
.pg-btn:hover { background: #f5f3f3; border-color: #9ca3af !important; }
.page-item.active .pg-btn {
    background: var(--ink) !important;
    border-color: var(--ink) !important;
    color: #97dab9 !important;
}
.page-item.disabled .pg-btn { opacity: .4; pointer-events: none; }
</style>